/**
 * Event photography, discovered at runtime instead of compiled in.
 *
 * Photos used to be `import.meta.glob`ed into the bundle and looked up in a
 * dictionary keyed by the event's *title string*. Three things were wrong with
 * that, and all three failed silently:
 *
 *   * the glob argument must be a literal, so a new event's photos needed a
 *     code change — dropping files into a new folder did nothing, with no error;
 *   * renaming an event lost its gallery, because a missed lookup renders null
 *     rather than complaining;
 *   * every frontend deploy shipped 619 MB of photography.
 *
 * Now `scripts/sync_assets.py` uploads photos to S3 keyed by event **id** and
 * publishes a manifest describing them. Adding photos is a sync, not a deploy.
 *
 * Failure here is deliberately quiet. A gallery that does not appear is the
 * same outcome an event with no photos already has, and it is not worth an
 * error banner over someone's registration form. It is a console warning and
 * an empty list.
 */

import { useEffect, useState } from 'react';

import { ASSETS_BASE_URL } from 'services/env';

export interface ManifestPhoto {
    /** Full-size image. */
    display: string;
    /**
     * Thumbnail-strip image. Equal to `display` until derivatives are
     * generated — the field is separate from the start so that adding them is a
     * re-sync rather than a change to this shape and every reader of it.
     */
    thumb: string;
}

interface ManifestEvent {
    /**
     * Both optional: the bulk sync knows the folder name and the event's title
     * and records them for whoever reads the manifest by hand, but the upload
     * endpoint (src/siteassets/admin.py) creates an entry from an event id
     * alone. Nothing here renders them — the photos are the contract.
     */
    slug?: string;
    title?: string;
    photos: ManifestPhoto[];
}

export interface AssetManifest {
    version: number;
    generated_at: string;
    events: Record<string, ManifestEvent>;
    collections: Record<string, { photos: ManifestPhoto[] }>;
}

const EMPTY: AssetManifest = { version: 0, generated_at: '', events: {}, collections: {} };

/**
 * One in-flight request per page load, shared by every gallery on it.
 *
 * Module-level rather than per-component state: the events page can mount
 * several galleries, and each fetching its own copy of the same manifest would
 * be pure waste.
 */
let inFlight: Promise<AssetManifest> | undefined;

export const fetchAssetManifest = (): Promise<AssetManifest> => {
    if (inFlight) {
        return inFlight;
    }
    if (!ASSETS_BASE_URL) {
        inFlight = Promise.resolve(EMPTY);
        return inFlight;
    }
    inFlight = fetch(`${ASSETS_BASE_URL.replace(/\/$/, '')}/manifest.json`)
        .then((response) => {
            if (!response.ok) {
                throw new Error(`manifest responded ${response.status}`);
            }
            return response.json() as Promise<AssetManifest>;
        })
        .catch((error: unknown) => {
            // eslint-disable-next-line no-console
            console.warn('Could not load the photo manifest; galleries will be empty.', error);
            return EMPTY;
        });
    return inFlight;
};

/** Test seam. Nothing in the app should need to call this. */
export const resetAssetManifest = () => {
    inFlight = undefined;
};

const absolute = (key: string) => `${(ASSETS_BASE_URL ?? '').replace(/\/$/, '')}/${key}`;

const withAbsoluteUrls = (photos: ManifestPhoto[]): ManifestPhoto[] =>
    photos.map((photo) => ({ display: absolute(photo.display), thumb: absolute(photo.thumb) }));

/**
 * Photos for one entry of the manifest.
 *
 * `kind` and `key` are both primitives, so the effect's dependencies are
 * exactly what it reads. Passing a selector function instead would change
 * identity on every render and need an exhaustive-deps escape hatch.
 */
const usePhotos = (kind: 'events' | 'collections', key?: string): ManifestPhoto[] => {
    const [photos, setPhotos] = useState<ManifestPhoto[]>([]);
    useEffect(() => {
        let cancelled = false;
        fetchAssetManifest()
            .then((manifest) => {
                const entry = key ? manifest[kind][key] : undefined;
                if (!cancelled) {
                    setPhotos(withAbsoluteUrls(entry?.photos ?? []));
                }
                return undefined;
            })
            // `fetchAssetManifest` resolves to an empty manifest rather than
            // rejecting, so this should never run. It is here because the
            // degradation must be the same either way: no photos, no banner.
            .catch(() => setPhotos([]));
        return () => {
            cancelled = true;
        };
    }, [kind, key]);
    return photos;
};

/**
 * Photos for one event, by id.
 *
 * An unknown id yields an empty list rather than throwing — an event with no
 * photos yet is the normal case every year until the first one is taken.
 */
export const useEventPhotos = (eventId?: string): ManifestPhoto[] => usePhotos('events', eventId);

/** Photos for a named non-event collection, such as `causes-supported`. */
export const useCollectionPhotos = (name: string): ManifestPhoto[] =>
    usePhotos('collections', name);
